/**
 * ReservedWalletAuditPanel — surface every customer whose `users.reserved`
 * is negative (bug from the pre-Phase-H45 race) OR drifts from the sum
 * of their open `order_reserve` transactions. Per-row Fix button applies
 * the same repair the API endpoint offers, but scoped to a single
 * customer at a time so the admin stays in control.
 *
 * Backend endpoints:
 *   GET  /api/admin/wallet/find-negative-reserved
 *   POST /api/admin/wallet/fix-negative-reserved
 *     body: { emails: [<one>], dry_run: false }
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReservedWalletAuditPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null); // email currently being fixed
  const [lastFix, setLastFix] = useState(null);     // {email, deleted, adjustment}
  const [confirmRow, setConfirmRow] = useState(null); // row awaiting confirmation

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/wallet/find-negative-reserved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        toast.error(`Audit failed (HTTP ${r.status})`);
        return;
      }
      const j = await r.json();
      setRows(j.rows || []);
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const applyFix = async (row) => {
    setBusyEmail(row.email);
    setConfirmRow(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/wallet/fix-negative-reserved`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emails: [row.email], dry_run: false }),
      });
      if (!r.ok) {
        toast.error(`Fix failed (HTTP ${r.status})`);
        return;
      }
      const j = await r.json();
      const fixed = (j.rows || [])[0];
      if (!fixed) {
        toast.warning('No changes applied — this customer may have been already clean.');
      } else {
        setLastFix({
          email:      fixed.email,
          deleted:    (fixed.duplicates_removed || []).reduce((n, d) => n + (d.deleted || 0), 0),
          adjustment: fixed.reserved_adjustment,
          balance_credit: fixed.balance_credit || 0,
          before:     fixed.before_reserved,
          after:      fixed.after_reserved,
        });
        const creditNote = fixed.balance_credit > 0
          ? ` — released €${fixed.balance_credit.toFixed(2)} back to balance`
          : '';
        toast.success(
          `Fixed ${fixed.email}: reserved ${fixed.before_reserved} → ${fixed.after_reserved} ` +
          `(${fixed.duplicates_removed?.length || 0} duplicate row(s) removed)${creditNote}`
        );
      }
      // Refresh so the row disappears / drift is now 0.
      await load();
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setBusyEmail(null);
    }
  };

  const totalDrift = rows.reduce((s, r) => s + Math.abs(r.drift || 0), 0);

  return (
    <section
      className="bg-white rounded-xl border border-amber-200 shadow-sm p-5 sm:p-6 mt-5"
      data-testid="reserved-wallet-audit-card"
    >
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-amber-600" /> Reserved wallet audit
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
            Customers whose <code>users.reserved</code> is negative or drifts from their
            open <code>order_reserve</code> transactions. One-click fix per row:
            dedupes duplicate <code>order_complete</code> rows and re-syncs{' '}
            <code>reserved</code> to ledger truth.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="reserved-audit-refresh"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {/* Last-fix summary */}
      {lastFix && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 mb-3 text-xs flex items-start gap-2"
          data-testid="reserved-audit-last-fix"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="text-emerald-900">
            <b>Last fix — {lastFix.email}</b>: reserved <b>€{lastFix.before?.toFixed(2)}</b> → <b>€{lastFix.after?.toFixed(2)}</b>{' '}
            (adjustment {lastFix.adjustment >= 0 ? '+' : ''}€{lastFix.adjustment?.toFixed(2)},{' '}
            {lastFix.deleted} duplicate row{lastFix.deleted === 1 ? '' : 's'} removed).
          </div>
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="reserved-audit-loading">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Scanning…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-center gap-2"
          data-testid="reserved-audit-empty"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          All clear — no customers with negative <code>reserved</code> or ledger drift.
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="text-[11px] text-slate-500 mb-2" data-testid="reserved-audit-summary">
            <b>{rows.length}</b> customer{rows.length === 1 ? '' : 's'} affected · total absolute drift <b>€{totalDrift.toFixed(2)}</b>
          </div>
          <div className="border border-amber-200 rounded-lg overflow-x-auto" data-testid="reserved-audit-table">
            <table className="w-full text-xs">
              <thead className="bg-amber-50">
                <tr>
                  <th className="px-2 py-1.5 text-left font-semibold text-slate-600">Email</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Stored reserved</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Ledger truth</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Drift</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Dup completes</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-amber-100 hover:bg-amber-50/40" data-testid={`reserved-audit-row-${r.user_id}`}>
                    <td className="px-2 py-1.5 font-mono">{r.email || <em className="text-slate-400">no email</em>}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${r.stored_reserved < 0 ? 'text-rose-600 font-bold' : 'text-slate-700'}`}>
                      €{r.stored_reserved?.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-medium">
                      €{r.computed_reserved?.toFixed(2)}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${r.drift > 0 ? 'text-emerald-600' : r.drift < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                      {r.drift > 0 ? '+' : ''}€{r.drift?.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {r.duplicate_completes?.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-rose-700 font-medium">
                          <AlertTriangle className="w-3 h-3" /> {r.duplicate_completes.length}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        onClick={() => setConfirmRow(r)}
                        disabled={busyEmail === r.email}
                        data-testid={`reserved-audit-fix-${r.user_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-600 text-white text-[11px] font-medium hover:bg-amber-700 disabled:opacity-50"
                        title="Dedupe order_complete rows and resync users.reserved to ledger truth"
                      >
                        {busyEmail === r.email ? (
                          <><Loader2 className="w-3 h-3 animate-spin" /> Fixing…</>
                        ) : (
                          <><Wrench className="w-3 h-3" /> Fix</>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Per-row confirmation modal — shows exact effect before writing */}
      {confirmRow && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setConfirmRow(null)}
          data-testid="reserved-audit-confirm-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5"
          >
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-slate-800">Confirm wallet repair</h4>
                <p className="text-xs text-slate-500 mt-0.5">Preview the exact change before applying.</p>
              </div>
            </div>
            {(() => {
              const stuckPositive = confirmRow.stored_reserved > 0 && confirmRow.drift < -0.001;
              const balanceCredit = stuckPositive ? Math.abs(confirmRow.drift) : 0;
              return (
                <>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1 mb-4">
                    <div><b>Email:</b> <span className="font-mono">{confirmRow.email}</span></div>
                    <div><b>Reserved before:</b> <span className={confirmRow.stored_reserved < 0 ? 'text-rose-600 font-bold' : ''}>€{confirmRow.stored_reserved?.toFixed(2)}</span></div>
                    <div><b>Reserved after:</b> <span className="text-emerald-700 font-bold">€{confirmRow.computed_reserved?.toFixed(2)}</span></div>
                    <div><b>Reserved adjustment:</b> {confirmRow.drift > 0 ? '+' : ''}€{confirmRow.drift?.toFixed(2)}</div>
                    {stuckPositive && (
                      <div className="pt-1 mt-1 border-t border-slate-200" data-testid="reserved-audit-balance-credit-row">
                        <b>Balance credit:</b>{' '}
                        <span className="text-emerald-700 font-bold">+€{balanceCredit.toFixed(2)}</span>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          Released reserve returns to customer&apos;s spendable balance.
                        </div>
                      </div>
                    )}
                    {confirmRow.duplicate_completes?.length > 0 && (
                      <div><b>Duplicate order_complete rows to delete:</b> {confirmRow.duplicate_completes.length}</div>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mb-3">
                    This will delete duplicate <code>order_complete</code> rows (keeping the oldest one)
                    and set <code>reserved</code> to the ledger-truth value.
                    {stuckPositive ? (
                      <> The released <b>€{balanceCredit.toFixed(2)}</b> will be credited back to the customer&apos;s <code>balance</code>, and an <code>admin_adjustment</code> audit row will be written to the ledger.</>
                    ) : (
                      <> Balances and orders are NOT modified.</>
                    )}
                  </p>
                </>
              );
            })()}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmRow(null)}
                data-testid="reserved-audit-confirm-cancel"
                className="px-4 py-1.5 text-xs rounded-md border border-slate-300 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => applyFix(confirmRow)}
                data-testid="reserved-audit-confirm-apply"
                className="px-4 py-1.5 text-xs rounded-md bg-amber-600 text-white font-medium hover:bg-amber-700"
              >
                Apply fix
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
